import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Play, Square, Clock, Plus, X, Timer, CheckCircle, HelpCircle, Loader2, Zap,
  ChevronDown, Sparkles, Settings, DollarSign, Calendar, ChevronLeft, ChevronRight,
  Pause, Target, Coffee, Lightbulb, Edit3, ChevronUp, Minimize2, Maximize2, PauseCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { useActiveTimer } from '../hooks/useActiveTimer';
import { useTimerData } from '../hooks/useTimerData';
import { supabase } from '../lib/supabase';
import CalendarView, { CalendarViewModeSelector } from './CalendarView';

const EnhancedTimerView = () => {
  const { user } = useAuth();
  const { currentTenant, userRole } = useTenant();
  
  const [showPomodoroHelp, setShowPomodoroHelp] = useState(false);
  
  // Use the timer data hook
  const { clients, projects, timeEntries, loading: dataLoading, createTimeEntry, updateTimeEntry, deleteTimeEntry, refetch } = useTimerData();
 
  // Mobile and UX state
  const [isMobile, setIsMobile] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  
  // Enhanced timer state
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [taskName, setTaskName] = useState('');
  const [description, setDescription] = useState('');
  const [isBillable, setIsBillable] = useState(true);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isStarting, setIsStarting] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [lastActiveProjects, setLastActiveProjects] = useState([]);
  
  // Floating timer widget state
  const [showFloatingTimer, setShowFloatingTimer] = useState(false);
  const [isFloatingMinimized, setIsFloatingMinimized] = useState(false);
  
  // Auto-pause detection state
  const [isAutoPaused, setIsAutoPaused] = useState(false);
  const [idleStartTime, setIdleStartTime] = useState(null);
  const [lastActivityTime, setLastActivityTime] = useState(Date.now());
  const [showIdleModal, setShowIdleModal] = useState(false);
  const [idleTime, setIdleTime] = useState(0);
  
  // Smart rounding state
  const [enableSmartRounding, setEnableSmartRounding] = useState(true);
  const [roundingInterval, setRoundingInterval] = useState(15); // minutes
  const [showRoundingOptions, setShowRoundingOptions] = useState(false);
  
  // Advanced features state
  const [isPaused, setIsPaused] = useState(false);
  const [pausedTime, setPausedTime] = useState(0);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showTimeGoals, setShowTimeGoals] = useState(false);
  const [dailyGoal, setDailyGoal] = useState(8);
  const [weeklyGoal, setWeeklyGoal] = useState(40);
  const [autoBreakReminder, setAutoBreakReminder] = useState(true);
  const [breakInterval, setBreakInterval] = useState(60); // minutes
  const [lastBreakTime, setLastBreakTime] = useState(null);
  const [showBreakReminder, setShowBreakReminder] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  
  // Calendar-specific state 
  const [viewMode, setViewMode] = useState('week'); // 'day', 'week', 'month'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [goals, setGoals] = useState({ dailyHours: 8, weeklyHours: 40 });

  // AI Suggestions
  const [showAISuggestions, setShowAISuggestions] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([
    { task: 'Code review for recent PR', confidence: 95, reason: 'Similar to yesterday at this time' },
    { task: 'Update project documentation', confidence: 78, reason: 'Often follows development work' },
    { task: 'Team standup prep', confidence: 85, reason: 'Scheduled for 10 AM' }
  ]);
  
  // Pomodoro Timer
  const [pomodoroMode, setPomodoroMode] = useState(false);
  const [pomodoroSession, setPomodoroSession] = useState(1);
  const [pomodoroType, setPomodoroType] = useState('work'); // 'work' or 'break'
  const [pomodoroSettings, setPomodoroSettings] = useState({
    workDuration: 25,
    shortBreak: 5,
    longBreak: 15,
    longBreakInterval: 4
  });
  
  // Time Tracking Templates
  const [templates, setTemplates] = useState([
    { id: '1', name: 'Daily Standup', client: '', project: '', task: 'Team standup meeting', duration: 0.25, billable: false },
    { id: '2', name: 'Code Review', client: '', project: '', task: 'Code review and feedback', duration: 1, billable: true },
    { id: '3', name: 'Client Meeting', client: '', project: '', task: 'Client status meeting', duration: 1, billable: true }
  ]);
  
  // Manual entry state
  const [manualDate, setManualDate] = useState(new Date().toISOString().split('T')[0]);
  const [manualHours, setManualHours] = useState('');
  const [manualMinutes, setManualMinutes] = useState('');
  
  const timerRef = useRef(null);
  const formRef = useRef(null);
  const activityTimeoutRef = useRef(null);
  const idleCheckIntervalRef = useRef(null);

  // Custom Hooks
  const { activeTimer, startTimer, stopTimer } = useActiveTimer();

  // Activity detection for auto-pause
  const resetActivityTimer = useCallback(() => {
    setLastActivityTime(Date.now());
    
    if (isAutoPaused && activeTimer) {
      // Resume from auto-pause
      setIsAutoPaused(false);
      setIsPaused(false);
      setIdleStartTime(null);
      setShowIdleModal(false);
    }
    
    // Clear existing timeout
    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current);
    }
    
    // Set new timeout for idle detection (5 minutes)
    if (activeTimer && !isPaused) {
      activityTimeoutRef.current = setTimeout(() => {
        setIsAutoPaused(true);
        setIdleStartTime(Date.now());
        setShowIdleModal(true);
      }, 5 * 60 * 1000); // 5 minutes
    }
  }, [activeTimer, isPaused, isAutoPaused]);

  // Set up activity monitoring
  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    
    // Add event listeners for activity detection
    events.forEach(event => {
      document.addEventListener(event, resetActivityTimer, true);
    });
    
    // Initialize activity timer if there's an active timer
    if (activeTimer && !isPaused) {
      resetActivityTimer();
    }
    
    return () => {
      // Cleanup
      events.forEach(event => {
        document.removeEventListener(event, resetActivityTimer, true);
      });
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current);
      }
    };
  }, [resetActivityTimer, activeTimer, isPaused]);

  // Show floating timer when there's an active timer and user scrolls away
  useEffect(() => {
    const handleScroll = () => {
      if (activeTimer) {
        const timerElement = document.querySelector('[data-timer-display]');
        if (timerElement) {
          const rect = timerElement.getBoundingClientRect();
          const isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;
          setShowFloatingTimer(!isVisible);
        }
      } else {
        setShowFloatingTimer(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [activeTimer]);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Enhanced timer effect with break reminders
  useEffect(() => {
    if (activeTimer && !isPaused) {
      const startTime = new Date(activeTimer.started_at || Date.now()).getTime();
      
      const updateTimer = () => {
        setElapsedTime(Date.now() - startTime);
        
        // Break reminder logic
        if (autoBreakReminder && !lastBreakTime) {
          setLastBreakTime(Date.now());
        } else if (autoBreakReminder && lastBreakTime) {
          const timeSinceBreak = (Date.now() - lastBreakTime) / (1000 * 60);
          if (timeSinceBreak >= breakInterval) {
            setShowBreakReminder(true);
          }
        }
      };

      updateTimer();
      timerRef.current = setInterval(updateTimer, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (!activeTimer) {
        setElapsedTime(0);
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [activeTimer, isPaused, autoBreakReminder, breakInterval, lastBreakTime]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case ' ':
            e.preventDefault();
            if (activeTimer) {
              handlePauseTimer();
            } else {
              handleStartTimer();
            }
            break;
          case 's':
            e.preventDefault();
            if (activeTimer) {
              handleStopTimer();
            }
            break;
          case 't':
            e.preventDefault();
            setShowTemplates(true);
            break;
          case 'k':
            e.preventDefault();
            setShowShortcuts(true);
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [activeTimer]);

  // Load last used projects for quick access
  useEffect(() => {
    if (timeEntries.length > 0) {
      const recentProjects = timeEntries
        .filter(entry => entry.client_id && entry.project_id)
        .sort((a, b) => new Date(b.entry_date) - new Date(a.entry_date))
        .slice(0, 3)
        .map(entry => {
          const client = clients.find(c => c.id === entry.client_id);
          const project = projects.find(p => p.id === entry.project_id);
          
          return {
            clientId: entry.client_id,
            projectId: entry.project_id,
            clientName: client?.name || 'Unknown Client',
            projectName: project?.name || 'Unknown Project',
            taskName: entry.task_name
          };
        });
      
      setLastActiveProjects(recentProjects);
    }
  }, [timeEntries, clients, projects]);

  // Validation function
  const validateTimerForm = useCallback(() => {
    const errors = {};
    
    if (!selectedClient) {
      errors.client = 'Please select a client';
    }
    if (!selectedProject) {
      errors.project = 'Please select a project';
    }
    if (!taskName.trim()) {
      errors.taskName = 'Please enter a task name';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [selectedClient, selectedProject, taskName]);

  // Helper functions
  const formatTime = (hours) => {
    const h = Math.floor(hours);
    const m = Math.floor((hours - h) * 60);
    return `${h}:${m.toString().padStart(2, '0')}`;
  };
  
  const formatCurrency = (amount) => `$${amount.toFixed(2)}`;
  
  const formatElapsedTime = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const getProjectsForClient = (clientId) => {
    return projects.filter(p => p.client_id === clientId);
  };

  const showSuccessMessage = (message, color) => {
    const notification = document.createElement('div');
    notification.className = `fixed top-4 left-4 right-4 md:left-auto md:right-4 md:w-auto bg-${color}-500 text-white px-4 py-3 rounded-lg shadow-lg z-50 flex items-center space-x-2`;
    notification.innerHTML = `
      <svg class="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
      </svg>
      <span class="flex-1">${message}</span>
    `;
    document.body.appendChild(notification);
    setTimeout(() => {
      if (document.body.contains(notification)) {
        document.body.removeChild(notification);
      }
    }, 3000);
  };

  // Enhanced timer handlers
  const handleStartTimer = async () => {
    if (!validateTimerForm()) {
      if (isMobile) {
        alert('Please fill in all required fields: Client, Project, and Task Name');
      } else {
        const firstErrorField = Object.keys(validationErrors)[0];
        const element = formRef.current?.querySelector(`[data-field="${firstErrorField}"]`);
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsStarting(true);
    
    try {
      const { error } = await startTimer({
        client_id: selectedClient,
        project_id: selectedProject,
        task_name: taskName,
        description,
        is_billable: isBillable,
      });

      if (error) {
        throw new Error(error);
      }
      
      showSuccessMessage('Timer started successfully!', 'green');
      setIsPaused(false);
      setPausedTime(0);
      setIsAutoPaused(false);
      resetActivityTimer(); // Start monitoring for idle time
      
    } catch (error) {
      console.error('Error starting timer:', error);
      alert('Error starting timer: ' + error.message);
    } finally {
      setIsStarting(false);
    }
  };

  const handlePauseTimer = () => {
    if (isPaused || isAutoPaused) {
      // Resuming
      setIsPaused(false);
      setIsAutoPaused(false);
      resetActivityTimer(); // Resume activity monitoring
    } else {
      // Pausing
      setIsPaused(true);
      
      // Clear activity monitoring when manually paused
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current);
      }
    }
  };

  const handleStopTimer = async () => {
    if (!activeTimer) return;
    setIsStopping(true);
    
    try {
      // First, stop the timer normally to create the database entry
      const { error } = await stopTimer();
      
      if (error) {
        throw new Error(error);
      }

      // The stopTimer already creates the entry, so we just need to refresh our local data
      await refetch(); // This will update the timeEntries state with the new entry
      
      showSuccessMessage('Timer stopped and entry saved!', 'blue');
      
    } catch (error) {
      console.error('Error stopping timer:', error);
      alert('Error stopping timer: ' + error.message);
    } finally {
      setIsStopping(false);
    }
  };

  // Handle idle time decisions
  const handleIdleTimeKeep = () => {
    // Keep the idle time, just resume
    setShowIdleModal(false);
    setIsAutoPaused(false);
    setIsPaused(false);
    resetActivityTimer();
  };

  const handleIdleTimeDiscard = () => {
    // Discard idle time by adjusting the start time
    if (idleStartTime && activeTimer) {
      const idleDuration = Date.now() - idleStartTime;
      const adjustedStartTime = new Date(Date.now() - (elapsedTime - idleDuration));
      
      // Note: In a real implementation, you'd want to update the start_time in the database
      // For now, we'll just adjust the local elapsed time
      setElapsedTime(prev => prev - idleDuration);
    }
    
    setShowIdleModal(false);
    setIsAutoPaused(false);
    setIsPaused(false);
    resetActivityTimer();
  };

  const handleQuickStart = (recentProject) => {
    setSelectedClient(recentProject.clientId);
    setSelectedProject(recentProject.projectId);
    setTaskName(recentProject.taskName);
  };

  const handleTemplateSelect = (template) => {
    if (template.client) setSelectedClient(template.client);
    if (template.project) setSelectedProject(template.project);
    setTaskName(template.task);
    setIsBillable(template.billable);
    setShowTemplates(false);
    
    // Auto-start timer for templates with duration
    if (template.duration > 0) {
      setTimeout(() => handleStartTimer(), 100);
    }
  };

  const handleTakeBreak = () => {
    setShowBreakReminder(false);
    setLastBreakTime(Date.now());
    
    if (activeTimer) {
      handlePauseTimer();
    }
    
    // Could trigger a break timer here
    setTimeout(() => {
      alert('Break time over! Ready to get back to work?');
    }, 5 * 60 * 1000); // 5 minute break
  };

  // Calendar entry handlers
  const handleEntryEdit = async (entryId, entryData) => {
    try {
      const { error } = await updateTimeEntry(entryId, entryData);
      
      if (error) {
        alert('Error updating entry: ' + error);
        return;
      }
      
      showSuccessMessage('Entry updated successfully!', 'green');
    } catch (error) {
      console.error('Error updating entry:', error);
      alert('Error updating entry: ' + error.message);
    }
  };

  const handleEntryDuplicate = async (entry) => {
    try {
      const duplicatedEntry = {
        client_id: entry.client_id,
        project_id: entry.project_id,
        task_name: `${entry.task_name} (Copy)`,
        description: entry.description,
        duration_hours: entry.duration_hours,
        is_billable: entry.is_billable,
        entry_date: new Date().toISOString().split('T')[0], // Today's date
        hourly_rate: entry.hourly_rate
      };

      const { error } = await createTimeEntry(duplicatedEntry);
      
      if (error) {
        alert('Error duplicating entry: ' + error);
        return;
      }
      
      showSuccessMessage('Entry duplicated successfully!', 'green');
    } catch (error) {
      console.error('Error duplicating entry:', error);
      alert('Error duplicating entry: ' + error.message);
    }
  };

  const handleEntryDelete = async (entryId) => {
    if (window.confirm('Are you sure you want to delete this entry?')) {
      try {
        const { error } = await deleteTimeEntry(entryId);
        
        if (error) {
          alert('Error deleting entry: ' + error);
          return;
        }
        
        showSuccessMessage('Entry deleted successfully!', 'red');
      } catch (error) {
        console.error('Error deleting entry:', error);
        alert('Error deleting entry: ' + error.message);
      }
    }
  };

  const handleEntryCreate = async (entryData) => {
    try {
      const { error } = await createTimeEntry(entryData);
      
      if (error) {
        alert('Error creating entry: ' + error);
        return;
      }
      
      showSuccessMessage('Entry created successfully!', 'green');
    } catch (error) {
      console.error('Error creating entry:', error);
      alert('Error creating entry: ' + error.message);
    }
  };

  const handleManualEntry = async () => {
    if (!selectedClient || !selectedProject || !taskName.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    const hours = parseInt(manualHours) || 0;
    const minutes = parseInt(manualMinutes) || 0;
    const totalHours = hours + (minutes / 60);

    if (totalHours <= 0) {
      alert('Please enter a valid time duration');
      return;
    }

    try {
      const entryData = {
        client_id: selectedClient,
        project_id: selectedProject,
        task_name: taskName,
        description,
        duration_hours: totalHours,
        is_billable: isBillable,
        entry_date: manualDate
      };

      const { error } = await createTimeEntry(entryData);
      
      if (error) {
        throw new Error(error);
      }
      
      showSuccessMessage('Manual entry added successfully!', 'green');
      
      // Reset form
      setManualHours('');
      setManualMinutes('');
      setShowManualEntry(false);
      
    } catch (error) {
      console.error('Error creating manual entry:', error);
      alert('Error creating manual entry: ' + error.message);
    }
  };

  const calculateDailyProgress = () => {
    const todayHours = timeEntries
      .filter(entry => entry.entry_date === new Date().toISOString().split('T')[0])
      .reduce((sum, entry) => sum + (entry.duration_hours || 0), 0);
    return (todayHours / dailyGoal) * 100;
  };

  const calculateWeeklyProgress = () => {
    const weekStart = new Date();
    weekStart.setDate(weekStart.getDate() - weekStart.getDay());
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    
    const weekHours = timeEntries
      .filter(entry => {
        const entryDate = new Date(entry.entry_date);
        return entryDate >= weekStart && entryDate <= weekEnd;
      })
      .reduce((sum, entry) => sum + (entry.duration_hours || 0), 0);
    return (weekHours / weeklyGoal) * 100;
  };

  // Calculate today's totals
  const todayEntries = timeEntries.filter(entry => 
    entry.entry_date === new Date().toISOString().split('T')[0]
  );
  
  const todayTotalHours = todayEntries.reduce((sum, entry) => sum + (entry.duration_hours || 0), 0);
  const todayBillableHours = todayEntries.filter(e => e.is_billable).reduce((sum, entry) => sum + (entry.duration_hours || 0), 0);

  // Show loading state
  if (dataLoading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading timer data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Calendar Section */}
      <div className="space-y-6">
        {/* Header with View Mode Switcher */}
        <div className="flex items-center justify-between">
          <CalendarViewModeSelector 
            viewMode={viewMode} 
            onViewModeChange={setViewMode} 
          />
        </div>

        {/* Calendar Interface */}
        <CalendarView
          timeEntries={timeEntries}
          clients={clients}
          projects={projects}
          currentDate={currentDate}
          viewMode={viewMode}
          onDateChange={setCurrentDate}
          onEntryEdit={handleEntryEdit}
          onEntryDuplicate={handleEntryDuplicate}
          onEntryDelete={handleEntryDelete}
          onEntryCreate={handleEntryCreate} 
          goals={goals}
        />
      </div>

      {/* Quick Start Section with AI Suggestions */}
      {!activeTimer && (lastActiveProjects.length > 0 || aiSuggestions.length > 0) && (
        <div className="bg-gradient-to-r from-purple-50 via-blue-50 to-indigo-50 rounded-2xl p-6 border border-purple-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Zap className="w-5 h-5 text-purple-600" />
              <h3 className="text-lg font-semibold text-slate-800">Quick Start</h3>
              <span className="text-sm text-slate-600 hidden sm:inline">Continue recent projects or try AI suggestions</span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowTemplates(true)}
                className="text-purple-600 text-sm font-medium hover:text-purple-700 flex items-center space-x-1"
              >
                <Plus className="w-4 h-4" />
                <span>Templates</span>
              </button>
              <span className="text-slate-300">|</span>
              <button
                onClick={() => setShowShortcuts(true)}
                className="text-slate-500 text-sm hover:text-slate-700"
                title="Keyboard Shortcuts"
              >
                ⌘K
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Recent Projects */}
            {lastActiveProjects.map((project, index) => (
              <button
                key={`project-${index}`}
                onClick={() => handleQuickStart(project)}
                className="bg-white hover:bg-gray-50 border border-purple-200 rounded-xl p-4 text-left transition-all transform hover:scale-105 hover:shadow-md"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="font-medium text-slate-800 text-sm">{project.projectName}</div>
                  <div className="text-xs text-purple-600 bg-purple-100 px-2 py-1 rounded-full">
                    Recent
                  </div>
                </div>
                <div className="text-xs text-slate-600 mb-2">{project.clientName}</div>
                <div className="text-sm text-purple-600 font-medium">{project.taskName}</div>
              </button>
            ))}

            {/* AI Suggestions - Only show first 2 */}
            {aiSuggestions.slice(0, 2).map((suggestion, index) => (
              <button
                key={`ai-${index}`}
                onClick={() => {
                  setTaskName(suggestion.task);
                }}
                className="bg-white hover:bg-gray-50 border border-indigo-200 rounded-xl p-4 text-left transition-all transform hover:scale-105 hover:shadow-md"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="font-medium text-slate-800 text-sm">{suggestion.task}</div>
                  <div className="flex items-center space-x-1">
                    <div className="w-4 h-4 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-sm flex items-center justify-center">
                      <span className="text-white text-xs font-bold">AI</span>
                    </div>
                    <div className="text-xs text-indigo-600 font-medium bg-indigo-100 px-2 py-1 rounded-full">
                      {suggestion.confidence}%
                    </div>
                  </div>
                </div>
                <div className="text-xs text-slate-500">{suggestion.reason}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Enhanced Active Timer Display */}
      <div 
        data-timer-display
        className={`rounded-2xl p-8 border shadow-lg transition-all duration-500 ${
          activeTimer 
            ? (isAutoPaused ? 'bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 border-orange-200 shadow-orange-100' :
               isPaused ? 'bg-gradient-to-br from-yellow-50 via-amber-50 to-orange-50 border-yellow-200 shadow-yellow-100' : 
               'bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 border-green-200 shadow-green-100')
            : 'bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 border-blue-100 shadow-blue-100'
        }`}
      >
        <div className="flex flex-col lg:flex-row lg:items-center lg:space-x-12 space-y-8 lg:space-y-0">
          
          {/* Timer Display with Advanced Controls */}
          <div className="flex-shrink-0">
            <div className="text-center">
              <div className={`text-6xl md:text-7xl font-mono font-bold mb-4 tracking-wider transition-colors duration-500 ${
                activeTimer ? (isAutoPaused ? 'text-orange-600' : 
                             isPaused ? 'text-yellow-600' : 'text-green-700') : 'text-slate-700'
              }`}>
                {activeTimer ? formatElapsedTime(elapsedTime) : '00:00:00'}
              </div>
              
              {/* Pomodoro Progress */}
              {pomodoroMode && activeTimer && (
                <div className="mb-4">
                  <div className="w-32 h-2 bg-slate-200 rounded-full mx-auto mb-2">
                    <div 
                      className="h-2 bg-red-500 rounded-full transition-all duration-300"
                      style={{ 
                        width: `${(elapsedTime / (pomodoroSettings.workDuration * 60 * 1000)) * 100}%` 
                      }}
                    />
                  </div>
                  <div className="text-sm text-slate-600">
                    Pomodoro {pomodoroSession} • {pomodoroType === 'work' ? 'Focus Time' : 'Break Time'}
                  </div>
                </div>
              )}
              
              {/* Timer Controls */}
              <div className="flex justify-center space-x-3">
                {!activeTimer ? (
                  <button
                    onClick={handleStartTimer}
                    disabled={isStarting || !taskName.trim()}
                    className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-8 py-4 rounded-xl flex items-center space-x-3 transition-all transform hover:scale-105 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:transform-none disabled:cursor-not-allowed"
                  >
                    <Play className="w-6 h-6" />
                    <span className="text-lg font-medium">Start Timer</span>
                  </button>
                ) : (
                  <>
                    <button
                      onClick={handlePauseTimer}
                      className={`px-6 py-3 rounded-xl flex items-center space-x-2 transition-all transform hover:scale-105 ${
                        isAutoPaused || isPaused
                          ? 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white'
                          : 'bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white'
                      }`}
                    >
                      {isAutoPaused || isPaused ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
                      <span>{isAutoPaused || isPaused ? 'Resume' : 'Pause'}</span>
                    </button>
                    
                    <button
                      onClick={handleStopTimer}
                      disabled={isStopping}
                      className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-6 py-3 rounded-xl flex items-center space-x-2 transition-all transform hover:scale-105 disabled:opacity-50"
                    >
                      {isStopping ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Square className="w-5 h-5" />
                      )}
                      <span>{isStopping ? 'Stopping...' : 'Stop'}</span>
                    </button>
                  </>
                )}
              </div>

              {/* Advanced Timer Controls and Settings */}
              <div className="flex justify-center space-x-2 mt-4">
                <button
                  onClick={() => setShowRoundingOptions(!showRoundingOptions)}
                  className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                    enableSmartRounding 
                      ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' 
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  Smart Rounding
                </button>
                
                <button
                  onClick={() => setPomodoroMode(!pomodoroMode)}
                  className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                    pomodoroMode 
                      ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  Pomodoro
                </button>
                
                <button
                  onClick={() => setShowConfigModal(true)}
                  className="px-3 py-2 bg-slate-100 text-slate-600 hover:bg-slate-200 rounded-lg text-sm transition-colors"
                >
                  <Settings className="w-4 h-4" />
                </button>
                

              </div>

              {/* Smart Rounding Options */}
              {showRoundingOptions && (
                <div className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-medium text-blue-900">Smart Time Rounding</span>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={enableSmartRounding}
                        onChange={(e) => setEnableSmartRounding(e.target.checked)}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-blue-700">Enable</span>
                    </label>
                  </div>
                  {enableSmartRounding && (
                    <div>
                      <label className="block text-sm font-medium text-blue-900 mb-2">
                        Round to nearest:
                      </label>
                      <select
                        value={roundingInterval}
                        onChange={(e) => setRoundingInterval(parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm"
                      >
                        <option value={5}>5 minutes</option>
                        <option value={10}>10 minutes</option>
                        <option value={15}>15 minutes</option>
                        <option value={30}>30 minutes</option>
                        <option value={60}>1 hour</option>
                      </select>
                    </div>
                  )}
                </div>
              )}


              
              {/* Timer Status Messages */}
              {isAutoPaused && (
                <div className="mt-4 p-3 bg-orange-100 border border-orange-200 rounded-lg">
                  <div className="flex items-center justify-center space-x-2 text-orange-700">
                    <PauseCircle className="w-5 h-5" />
                    <span className="font-medium">Auto-paused due to inactivity</span>
                  </div>
                  <div className="text-sm text-orange-600 mt-1">
                    Move your mouse or press any key to resume
                  </div>
                </div>
              )}
              
              {isPaused && !isAutoPaused && (
                <div className="mt-4 p-3 bg-yellow-100 border border-yellow-200 rounded-lg">
                  <div className="flex items-center justify-center space-x-2 text-yellow-700">
                    <Pause className="w-5 h-5" />
                    <span className="font-medium">Timer paused</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Timer Form */}
          <div className="flex-1" ref={formRef}>
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Client *</label>
                  <select
                    data-field="client"
                    value={selectedClient}
                    onChange={(e) => {
                      setSelectedClient(e.target.value);
                      setSelectedProject(''); // Reset project when client changes
                      setValidationErrors(prev => ({ ...prev, client: '' }));
                    }}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    disabled={activeTimer}
                  >
                    <option value="">Select a client</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>{client.name}</option>
                    ))}
                  </select>
                  {validationErrors.client && (
                    <p className="text-red-600 text-sm mt-1">{validationErrors.client}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Project *</label>
                  <select
                    data-field="project"
                    value={selectedProject}
                    onChange={(e) => {
                      setSelectedProject(e.target.value);
                      setValidationErrors(prev => ({ ...prev, project: '' }));
                    }}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    disabled={activeTimer || !selectedClient}
                  >
                    <option value="">Select a project</option>
                    {getProjectsForClient(selectedClient).map(project => (
                      <option key={project.id} value={project.id}>{project.name}</option>
                    ))}
                  </select>
                  {validationErrors.project && (
                    <p className="text-red-600 text-sm mt-1">{validationErrors.project}</p>
                  )}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Task Name *</label>
                <input
                  data-field="taskName"
                  type="text"
                  value={taskName}
                  onChange={(e) => {
                    setTaskName(e.target.value);
                    setValidationErrors(prev => ({ ...prev, taskName: '' }));
                  }}
                  placeholder="What are you working on?"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={activeTimer}
                />
                {validationErrors.taskName && (
                  <p className="text-red-600 text-sm mt-1">{validationErrors.taskName}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Add details about your work..."
                  rows={3}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  disabled={activeTimer}
                />
              </div>
              
              <div>
                <label className="flex items-center space-x-3 p-4 bg-slate-50 rounded-lg border border-slate-200 hover:bg-slate-100 transition-colors cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isBillable}
                    onChange={(e) => setIsBillable(e.target.checked)}
                    className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                    disabled={activeTimer}
                  />
                  <div className="flex-1">
                    <div className="font-medium text-slate-700">This time is billable</div>
                    <div className="text-sm text-slate-500">Check if this work should be billed to the client</div>
                  </div>
                  <DollarSign className="w-5 h-5 text-green-600" />
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Today's Summary and Progress */}
        {showTimeGoals && (
          <div className="mt-8 pt-6 border-t border-slate-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-600">Today's Time</span>
                  <Clock className="w-4 h-4 text-blue-500" />
                </div>
                <div className="text-2xl font-bold text-slate-900">{formatTime(todayTotalHours)}</div>
                <div className="text-sm text-slate-500">
                  {formatTime(todayBillableHours)} billable
                </div>
              </div>
              
              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-600">Entries Today</span>
                  <CheckCircle className="w-4 h-4 text-purple-500" />
                </div>
                <div className="text-2xl font-bold text-slate-900">{todayEntries.length}</div>
                <div className="text-sm text-slate-500">
                  Time entries completed
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700">Daily Goal Progress</span>
                  <span className="text-sm font-bold text-slate-800">
                    {Math.round(calculateDailyProgress())}%
                  </span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-blue-400 to-blue-600 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(calculateDailyProgress(), 100)}%` }}
                  ></div>
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {formatTime(todayTotalHours)} / {formatTime(dailyGoal)} hours
                </div>
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700">Weekly Goal Progress</span>
                  <span className="text-sm font-bold text-slate-800">
                    {Math.round(calculateWeeklyProgress())}%
                  </span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-green-400 to-green-600 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(calculateWeeklyProgress(), 100)}%` }}
                  ></div>
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  Goal: {formatTime(weeklyGoal)} hours this week
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Floating Timer Widget */}
      {showFloatingTimer && activeTimer && (
        <div className={`fixed top-4 right-4 z-50 bg-white rounded-2xl shadow-2xl border border-slate-200 transition-all duration-300 ${
          isFloatingMinimized ? 'w-16 h-16' : 'w-80'
        }`}>
          {isFloatingMinimized ? (
            // Minimized floating timer
            <button
              onClick={() => setIsFloatingMinimized(false)}
              className="w-16 h-16 flex items-center justify-center text-white bg-gradient-to-r from-green-500 to-green-600 rounded-2xl hover:from-green-600 hover:to-green-700 transition-all"
            >
              <Timer className="w-6 h-6" />
            </button>
          ) : (
            // Expanded floating timer
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <div className={`w-2 h-2 rounded-full ${
                    isAutoPaused ? 'bg-orange-500 animate-pulse' :
                    isPaused ? 'bg-yellow-500' : 'bg-green-500 animate-pulse'
                  }`}></div>
                  <span className="text-sm font-medium text-slate-700">
                    {formatElapsedTime(elapsedTime)}
                  </span>
                </div>
                <button
                  onClick={() => setIsFloatingMinimized(true)}
                  className="text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <Minimize2 className="w-4 h-4" />
                </button>
              </div>
              
              <div className="text-sm text-slate-600 mb-3 truncate">
                {taskName || 'Untitled Task'}
              </div>
              
              <div className="flex justify-between space-x-2">
                <button
                  onClick={handlePauseTimer}
                  className="flex-1 flex items-center justify-center space-x-1 py-2 text-sm rounded-lg transition-colors bg-slate-100 hover:bg-slate-200 text-slate-700"
                >
                  {isAutoPaused || isPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                  <span>{isAutoPaused || isPaused ? 'Resume' : 'Pause'}</span>
                </button>
                
                <button
                  onClick={handleStopTimer}
                  className="flex-1 flex items-center justify-center space-x-1 py-2 text-sm bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                >
                  <Square className="w-4 h-4" />
                  <span>Stop</span>
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Idle Time Modal */}
      {showIdleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">
                Welcome back!
              </h3>
              <p className="text-slate-600">
                You've been away for {Math.round((Date.now() - idleStartTime) / (1000 * 60))} minutes. 
                What would you like to do with this idle time?
              </p>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={handleIdleTimeKeep}
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Keep Time
              </button>
              <button
                onClick={handleIdleTimeDiscard}
                className="flex-1 px-4 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Discard Idle Time
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Break Reminder Modal */}
      {showBreakReminder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Coffee className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">
                Time for a break!
              </h3>
              <p className="text-slate-600">
                You've been working for {breakInterval} minutes. 
                Taking regular breaks helps maintain focus and productivity.
              </p>
            </div>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => setShowBreakReminder(false)}
                className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Remind Later
              </button>
              <button
                onClick={handleTakeBreak}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Take Break
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Templates Modal */}
      {showTemplates && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h3 className="text-xl font-semibold text-slate-900">Time Entry Templates</h3>
              <button
                onClick={() => setShowTemplates(false)}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {templates.map(template => (
                  <button
                    key={template.id}
                    onClick={() => handleTemplateSelect(template)}
                    className="p-4 border border-slate-200 rounded-xl hover:border-blue-300 hover:bg-blue-50 transition-all text-left"
                  >
                    <div className="font-medium text-slate-900 mb-2">{template.name}</div>
                    <div className="text-sm text-slate-600 mb-2">{template.task}</div>
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <span>{template.duration}h</span>
                      <span className={`px-2 py-1 rounded-full ${
                        template.billable ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-600'
                      }`}>
                        {template.billable ? 'Billable' : 'Non-billable'}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Keyboard Shortcuts Modal */}
      {showShortcuts && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h3 className="text-xl font-semibold text-slate-900">Keyboard Shortcuts</h3>
              <button
                onClick={() => setShowShortcuts(false)}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-slate-700">Start/Pause Timer</span>
                <kbd className="px-2 py-1 bg-slate-100 border border-slate-300 rounded text-sm">⌘ + Space</kbd>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-700">Stop Timer</span>
                <kbd className="px-2 py-1 bg-slate-100 border border-slate-300 rounded text-sm">⌘ + S</kbd>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-700">Open Templates</span>
                <kbd className="px-2 py-1 bg-slate-100 border border-slate-300 rounded text-sm">⌘ + T</kbd>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-700">Show Shortcuts</span>
                <kbd className="px-2 py-1 bg-slate-100 border border-slate-300 rounded text-sm">⌘ + K</kbd>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Configuration Modal */}
      {showConfigModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h3 className="text-xl font-semibold text-slate-900">Timer Configuration</h3>
              <button
                onClick={() => setShowConfigModal(false)}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Time Goals */}
              <div>
                <h4 className="font-medium text-slate-900 mb-3 flex items-center space-x-2">
                  <Target className="w-5 h-5 text-blue-600" />
                  <span>Time Goals</span>
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Daily Goal (hours)</label>
                    <input
                      type="number"
                      value={dailyGoal}
                      onChange={(e) => setDailyGoal(parseFloat(e.target.value))}
                      min="0"
                      step="0.5"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Weekly Goal (hours)</label>
                    <input
                      type="number"
                      value={weeklyGoal}
                      onChange={(e) => setWeeklyGoal(parseFloat(e.target.value))}
                      min="0"
                      step="0.5"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Break Reminders */}
              <div>
                <h4 className="font-medium text-slate-900 mb-3 flex items-center space-x-2">
                  <Coffee className="w-5 h-5 text-green-600" />
                  <span>Break Reminders</span>
                </h4>
                <div className="space-y-4">
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={autoBreakReminder}
                      onChange={(e) => setAutoBreakReminder(e.target.checked)}
                      className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-slate-700">Enable automatic break reminders</span>
                  </label>
                  {autoBreakReminder && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Remind me every (minutes)
                      </label>
                      <select
                        value={breakInterval}
                        onChange={(e) => setBreakInterval(parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      >
                        <option value={30}>30 minutes</option>
                        <option value={45}>45 minutes</option>
                        <option value={60}>1 hour</option>
                        <option value={90}>1.5 hours</option>
                        <option value={120}>2 hours</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>

              {/* Pomodoro Settings */}
              <div>
                <h4 className="font-medium text-slate-900 mb-3 flex items-center space-x-2">
                  <Timer className="w-5 h-5 text-red-600" />
                  <span>Pomodoro Settings</span>
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Work Duration</label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        value={pomodoroSettings.workDuration}
                        onChange={(e) => setPomodoroSettings(prev => ({ ...prev, workDuration: parseInt(e.target.value) }))}
                        min="1"
                        max="60"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="text-sm text-slate-600">min</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Short Break</label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        value={pomodoroSettings.shortBreak}
                        onChange={(e) => setPomodoroSettings(prev => ({ ...prev, shortBreak: parseInt(e.target.value) }))}
                        min="1"
                        max="30"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="text-sm text-slate-600">min</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Long Break</label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        value={pomodoroSettings.longBreak}
                        onChange={(e) => setPomodoroSettings(prev => ({ ...prev, longBreak: parseInt(e.target.value) }))}
                        min="1"
                        max="60"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="text-sm text-slate-600">min</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Long Break After</label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        value={pomodoroSettings.longBreakInterval}
                        onChange={(e) => setPomodoroSettings(prev => ({ ...prev, longBreakInterval: parseInt(e.target.value) }))}
                        min="1"
                        max="10"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="text-sm text-slate-600">sessions</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Smart Rounding */}
              <div>
                <h4 className="font-medium text-slate-900 mb-3 flex items-center space-x-2">
                  <Clock className="w-5 h-5 text-purple-600" />
                  <span>Smart Rounding</span>
                </h4>
                <div className="space-y-4">
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={enableSmartRounding}
                      onChange={(e) => setEnableSmartRounding(e.target.checked)}
                      className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-slate-700">Enable smart time rounding</span>
                  </label>
                  {enableSmartRounding && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Round to nearest
                      </label>
                      <select
                        value={roundingInterval}
                        onChange={(e) => setRoundingInterval(parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      >
                        <option value={5}>5 minutes</option>
                        <option value={10}>10 minutes</option>
                        <option value={15}>15 minutes</option>
                        <option value={30}>30 minutes</option>
                        <option value={60}>1 hour</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-4 p-6 border-t border-slate-200">
              <button
                onClick={() => setShowConfigModal(false)}
                className="px-6 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setGoals({ dailyHours: dailyGoal, weeklyHours: weeklyGoal });
                  setShowConfigModal(false);
                  showSuccessMessage('Configuration saved!', 'green');
                }}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Goal Settings Toggle - Commented out for now */}
      {/* 
      <div className="flex justify-center">
        <button
          onClick={() => setShowTimeGoals(!showTimeGoals)}
          className="flex items-center space-x-2 px-4 py-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <Target className="w-4 h-4" />
          <span>{showTimeGoals ? 'Hide' : 'Show'} Time Goals</span>
          <ChevronDown className={`w-4 h-4 transition-transform ${showTimeGoals ? 'rotate-180' : ''}`} />
        </button>
      </div>
      */}
    </div>
  );
};

export default EnhancedTimerView;